import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { RhDefaultProps } from '../common/rh-default-props';
import { useRhCustomContext } from '../rh-form-wrapper';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';
import { DNumberField } from '../../../inputs';

interface IRhNumberFieldProps extends Omit<RhDefaultProps, 'helperText'> {
    InputProps?: Partial<StandardInputProps>;
    // Widened from the shared string-only prop (like RHTextField) so callers can
    // pass a node — e.g. the transfer amount's "Aplicar" hint link.
    helperText?: React.ReactNode;
}

// react-hook-form wiring around the standalone `DNumberField` (which owns all
// the NumericFormat/MUI presentation). The value STORED in the form is the
// unformatted numeric string (no commas), so `yup.number()` coerces it cleanly.
// Drop-in for a numeric `RHTextField` (same prop surface).
export function RhNumberField({
    name,
    label,
    helperText,
    disableGutters = false,
    onChange,
    disabled = false,
    InputProps,
}: IRhNumberFieldProps) {
    const { control } = useFormContext();

    const { field, fieldState } = useController({
        name,
        control,
    });

    const { disabled: disabledFromContext } = useRhCustomContext();

    return (
        <DNumberField
            value={field.value}
            onChange={(value, source) => {
                // Store every change; mirror RHTextField semantics for the
                // external onChange — it is a *user-typed* signal, so only fire
                // it for real input events, not programmatic updates ('prop').
                field.onChange(value);
                if (onChange && source === 'event') {
                    onChange(value);
                }
            }}
            inputRef={field.ref}
            onBlur={field.onBlur}
            name={name}
            label={label}
            InputProps={InputProps}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message || helperText || ''}
            disabled={disabledFromContext || disabled}
            disableGutters={disableGutters}
        />
    );
}
